import React, { useCallback, useContext, useMemo, useState } from 'react';
import { ActivityIndicator, Modal, RefreshControl, ScrollView, TouchableOpacity, View, Image } from 'react-native';
import { useRoute } from '@react-navigation/native';

import ServiceNoteButton from '../driver/components/ServiceNoteButton';
import { GET_MEDIAS } from '../../../common/graphql/media';
import { Text } from '../../components';
import { ImageViewer } from '../../components/image';
import { EmptyState, Screen } from '../../design/components';
import PageHeading from '../../design/components/PageHeading';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { formatDate } from '../../../common/utils/utils';
import { ListContainer, ListContext } from '../../containers';

const MachinePhotoHistoryListRaw = ({ machineName, excludeDocketId }) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const [refreshing, setRefreshing] = useState(false);
	const [viewerState, setViewerState] = useState({ index: null, images: [] });
	const {
		items = [],
		loadingInitial,
		refetch,
	} = useContext(ListContext) || {};

	const groupedPhotos = useMemo(() => {
		const order = [];
		const groups = new Map();

		(items || []).forEach((media) => {
			const serviceDocket = media?.serviceDocket;
			if (!serviceDocket?.id) {
				return;
			}
			if (excludeDocketId && serviceDocket.id === excludeDocketId) {
				return;
			}

			if (!groups.has(serviceDocket.id)) {
				const planned = serviceDocket.serviceRun?.planned;
				const routeName = serviceDocket.serviceRun?.route?.name || serviceDocket.serviceRun?.name;
				const humanDate = planned ? formatDate(planned, 'DAY_MONTHWORD_YEAR') : 'Undated';
				const titleParts = [humanDate];
				if (routeName) {
					titleParts.push(routeName);
				}
				groups.set(serviceDocket.id, {
					id: serviceDocket.id,
					title: titleParts.join(' - '),
					photos: [],
				});
				order.push(groups.get(serviceDocket.id));
			}

			groups.get(serviceDocket.id).photos.push(media);
		});

		return order;
	}, [items, excludeDocketId]);

	const onRefresh = useCallback(async () => {
		if (!refetch) {
			return;
		}
		setRefreshing(true);
		try {
			await refetch();
		}
		finally {
			setRefreshing(false);
		}
	}, [refetch]);

	const openViewer = useCallback((photos, index) => {
		setViewerState({
			index,
			images: photos.map((photo) => ({ url: photo.link })),
		});
	}, []);

	const closeViewer = useCallback(() => {
		setViewerState({ index: null, images: [] });
	}, []);

	const renderBody = () => {
		if (loadingInitial) {
			return (
				<ActivityIndicator size="large" color={colors.accent} style={styles.loader} />
			);
		}

		if (!groupedPhotos.length) {
			return (
				<EmptyState icon="image" title="No previous photos" message="Photos taken at this machine will appear here." />
			);
		}

		return groupedPhotos.map((group) => (
			<View key={group.id} style={styles.group}>
				<Text style={styles.groupTitle}>{group.title}</Text>
				<View style={styles.photoGrid}>
					{group.photos.map((photo, index) => (
						<TouchableOpacity
							key={photo.id}
							style={styles.photoWrapper}
							onPress={() => openViewer(group.photos, index)}
							accessibilityRole="imagebutton"
							accessibilityLabel={`${group.title} photo ${index + 1}`}
						>
							<Image source={{ uri: photo.link }} style={styles.photo} />
						</TouchableOpacity>
					))}
				</View>
			</View>
		));
	};

	return (
		<View style={styles.container}>
			<ScrollView
				style={styles.scrollView}
				contentContainerStyle={styles.scrollContent}
				refreshControl={
					refetch
						? (
							<RefreshControl
								refreshing={refreshing}
								onRefresh={onRefresh}
							/>
						)
						: undefined
				}
			>
				<PageHeading
					title="Previous photos"
					subtitle={machineName}
				/>
				{renderBody()}
			</ScrollView>
			{
				viewerState.index !== null && (
					<Modal
						visible={true}
						transparent={true}
						onRequestClose={closeViewer}
					>
						<ImageViewer
							close={closeViewer}
							index={viewerState.index}
							imageUrls={viewerState.images}
						/>
					</Modal>
				)
			}
		</View>
	);
};

const MachinePhotoHistoryList = ListContainer(MachinePhotoHistoryListRaw);

const MachinePhotoHistoryView = () => {
	const route = useRoute();
	const { machineId, machineName, excludeDocketId } = route.params || {};
	const styles = useThemedStyles(createStyles);

	if (!machineId) {
		return (
			<Screen headerRight={<ServiceNoteButton />} heading="Previous photos">
				<Text style={styles.message}>Machine information missing.</Text>
			</Screen>
		);
	}

	return (
		<Screen headerRight={<ServiceNoteButton />} scroll={false}>
			<MachinePhotoHistoryList
				machineName={machineName}
				excludeDocketId={excludeDocketId}
				query={GET_MEDIAS}
				initialFilter={{ machine: machineId, docketExists: true }}
				initialSortBy="created"
				initialSortDirection={-1}
				initialPerPage={20}
			/>
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	container: {
		flex: 1,
	},
	scrollView: {
		flex: 1,
	},
	scrollContent: {
		paddingHorizontal: space.gutter,
		paddingTop: 20,
		paddingBottom: space.xl,
		gap: space.gutter,
	},
	loader: {
		marginTop: 20,
	},
	message: {
		...type.body,
		color: colors.textSecondary,
	},
	group: {
		gap: space.md,
	},
	groupTitle: {
		...type.sectionTitle,
		color: colors.text,
	},
	photoGrid: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: space.sm,
	},
	photoWrapper: {
		borderRadius: radius.control,
		overflow: 'hidden',
	},
	photo: {
		width: 96,
		height: 96,
		borderRadius: radius.control,
		backgroundColor: colors.chip,
	},
});

export default MachinePhotoHistoryView;
